'use client';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useColorScheme,
} from '@mui/material';
import type {
  CreateGraphInputChartTypeEnum,
  UpdateGraphInputChartTypeEnum,
} from '@repo/api-gw-sdk';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';

import { SqlEngineSelector } from '@/app/(dashboard)/lakehouse/query-editor/sqlEngineSelector';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import type { ChartType, ColumnMapping } from '@/types/dashboard';

import { ChartRenderer } from '../chartRenderer';
import { ChartTypePicker } from '../chartTypePicker';
import { ColumnMappingBar } from '../columnMappingBar';
import type { GraphWizardState } from '../createGraphFlow';
import { SavedQueryPicker } from '../savedQueryPicker';

const MonacoEditor = dynamic(() => import('@monaco-editor/react'), {
  ssr: false,
  loading: () => <Skeleton variant='rectangular' height='100%' />,
});

const POLL_INTERVAL_MS = 1000;

/** Coerce string values that look numeric into actual numbers for Recharts. */
function coerceValue(v: unknown): unknown {
  if (typeof v === 'string' && v !== '' && !isNaN(Number(v))) return Number(v);
  return v;
}

export function GraphEditorStep(props: StepProps<GraphWizardState>) {
  const { wizardState, setWizardState } = props;
  const dal = useDAL();
  const dalRef = useRef(dal);
  const { mode } = useColorScheme();
  const isDarkMode = mode === 'dark';
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    dalRef.current = dal;
  }, [dal]);

  const [selectedEngineId, setSelectedEngineId] = useState<string | null>(null);

  // Local UI state (not part of wizard state)
  const [queryData, setQueryData] = useState<Record<string, unknown>[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const autoExecutedRef = useRef(false);

  // Load existing graph if editing
  useEffect(() => {
    if (!wizardState.graphId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const graph = await dalRef.current.lakehouse.getGraph(
          wizardState.workspaceId,
          wizardState.dashboardId,
          wizardState.graphId!
        );
        if (cancelled || !graph) return;
        setWizardState((s) => ({
          ...s,
          title: graph.title,
          chartType: graph.chartType as ChartType,
          sqlQuery: graph.sqlQuery,
          columnMapping: graph.columnMapping as ColumnMapping,
          layout: graph.layout as typeof s.layout,
        }));
      } catch {
        // Graph not found
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [
    wizardState.workspaceId,
    wizardState.dashboardId,
    wizardState.graphId,
    setWizardState,
  ]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const executeQuery = useCallback(async () => {
    if (!wizardState.sqlQuery.trim() || !selectedEngineId || isExecuting)
      return;
    setIsExecuting(true);
    setError(null);

    try {
      const execution = await dalRef.current.lakehouse.startSqlQueryExecution(
        wizardState.workspaceId,
        { sqlEngineId: selectedEngineId, query: wizardState.sqlQuery }
      );
      if (!execution?.id) {
        setIsExecuting(false);
        setError('Failed to start query.');
        return;
      }

      const executionId = execution.id;

      const pollStatus = async () => {
        try {
          const status = await dalRef.current.lakehouse.getSqlQueryExecution(
            wizardState.workspaceId,
            executionId
          );

          if (status?.status === 'SUCCEEDED') {
            if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
            const result =
              await dalRef.current.lakehouse.getSqlQueryExecutionResult(
                wizardState.workspaceId,
                executionId
              );
            if (result?.rows && result?.columns) {
              const colNames = result.columns.map(
                (c) => c.name ?? `col_${String(result.columns.indexOf(c))}`
              );
              const rows: Record<string, unknown>[] = (
                result.rows as unknown[][]
              ).map((row) =>
                Object.fromEntries(
                  colNames.map((name, i) => [name, coerceValue(row[i])])
                )
              );
              setQueryData(rows);
              setColumns(colNames);
              setAnimationKey((k) => k + 1);
            }
            setIsExecuting(false);
          } else if (
            status?.status === 'FAILED' ||
            status?.status === 'CANCELLED'
          ) {
            if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
            setError(
              status?.status === 'FAILED' ? 'Query failed' : 'Query cancelled'
            );
            setIsExecuting(false);
          }
        } catch (err) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          setError(String(err));
          setIsExecuting(false);
        }
      };

      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => void pollStatus(), POLL_INTERVAL_MS);
    } catch (err) {
      setError(String(err));
      setIsExecuting(false);
    }
  }, [
    wizardState.sqlQuery,
    wizardState.workspaceId,
    selectedEngineId,
    isExecuting,
  ]);

  // Auto-execute query when editing an existing graph (once engine + query are ready)
  useEffect(() => {
    if (
      wizardState.graphId &&
      selectedEngineId &&
      wizardState.sqlQuery.trim() &&
      wizardState.sqlQuery !== '-- Write your SQL query here\n' &&
      !autoExecutedRef.current
    ) {
      autoExecutedRef.current = true;
      // Defer to avoid setState-in-effect lint warning
      const timer = setTimeout(() => void executeQuery(), 0);
      return () => clearTimeout(timer);
    }
  }, [
    wizardState.graphId,
    selectedEngineId,
    wizardState.sqlQuery,
    executeQuery,
  ]);

  const handleSave = useCallback(async () => {
    try {
      if (wizardState.graphId) {
        await dalRef.current.lakehouse.updateGraph(
          wizardState.workspaceId,
          wizardState.dashboardId,
          wizardState.graphId,
          {
            title: wizardState.title,
            chartType:
              wizardState.chartType as unknown as UpdateGraphInputChartTypeEnum,
            sqlQuery: wizardState.sqlQuery,
            columnMapping: wizardState.columnMapping,
            layout: wizardState.layout,
          }
        );
      } else {
        await dalRef.current.lakehouse.createGraph(
          wizardState.workspaceId,
          wizardState.dashboardId,
          {
            title: wizardState.title,
            chartType:
              wizardState.chartType as unknown as CreateGraphInputChartTypeEnum,
            sqlQuery: wizardState.sqlQuery,
            columnMapping: wizardState.columnMapping,
            layout: wizardState.layout,
          }
        );
      }
      await props.finish('Graph saved successfully');
    } catch {
      setError('Failed to save graph');
    }
  }, [wizardState, props]);

  // Keyboard shortcut: Ctrl/Cmd+Enter to execute
  const handleEditorKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        void executeQuery();
      }
    },
    [executeQuery]
  );

  return (
    <PanelWrapper
      footer={{
        primaryButton: {
          text: 'Save',
          onClick: () => void handleSave(),
          'data-testid': 'save-graph-button',
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: props.back,
          'data-testid': 'cancel-graph-button',
        },
      }}
    >
      <Stack
        direction='row'
        sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}
      >
        {/* LEFT PANEL — Query editor */}
        <Stack
          sx={{ width: '50%', height: '100%', minWidth: 0, overflow: 'hidden' }}
        >
          {/* Editor toolbar */}
          <Stack
            direction='row'
            alignItems='center'
            gap='8px'
            sx={{ px: '16px', py: '8px', minHeight: '44px' }}
          >
            <TextField
              value={wizardState.title}
              onChange={(e) =>
                setWizardState((s) => ({ ...s, title: e.target.value }))
              }
              variant='outlined'
              placeholder='Graph title'
              size='small'
              sx={{
                width: '200px',
                '& .MuiOutlinedInput-root': {
                  fontWeight: 600,
                  fontSize: '0.875rem',
                },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <SavedQueryPicker
              workspaceId={wizardState.workspaceId}
              onSelect={(query) =>
                setWizardState((s) => ({ ...s, sqlQuery: query }))
              }
            />
            <Box sx={{ width: '200px' }}>
              <SqlEngineSelector
                selectedEngineId={selectedEngineId}
                onEngineSelected={setSelectedEngineId}
                disabled={isExecuting}
                workspaceIdOverride={wizardState.workspaceId}
              />
            </Box>
            <Tooltip title='Cmd/Ctrl + Enter'>
              <span>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => void executeQuery()}
                  disabled={
                    isExecuting ||
                    !wizardState.sqlQuery.trim() ||
                    !selectedEngineId
                  }
                  size='small'
                  sx={{
                    minWidth: 0,
                    px: '12px',
                    backgroundColor: 'var(--mui-palette-info-main)',
                    '&:hover': {
                      backgroundColor: 'var(--mui-palette-info-dark)',
                    },
                  }}
                  startIcon={
                    isExecuting ? (
                      <CircularProgress size={14} color='inherit' />
                    ) : (
                      <i
                        className='material-symbols-play-arrow-rounded'
                        style={{ fontSize: '16px' }}
                      />
                    )
                  }
                >
                  Run
                </Button>
              </span>
            </Tooltip>
          </Stack>

          {/* Monaco editor */}
          <Box
            sx={{ flexGrow: 1, minHeight: 0 }}
            onKeyDown={handleEditorKeyDown}
          >
            <MonacoEditor
              height='100%'
              language='sql'
              theme={isDarkMode ? 'vs-dark' : 'light'}
              value={wizardState.sqlQuery}
              onChange={(value) =>
                setWizardState((s) => ({ ...s, sqlQuery: value ?? '' }))
              }
              options={{
                minimap: { enabled: false },
                overviewRulerLanes: 0,
                readOnly: isExecuting,
                quickSuggestions: true,
                scrollBeyondLastLine: false,
                fontSize: 13,
                lineNumbers: 'on',
                folding: true,
                automaticLayout: true,
                wordWrap: 'on',
                padding: { top: 8 },
              }}
            />
          </Box>

          {/* Error message */}
          {error && (
            <Box
              sx={{
                px: '16px',
                py: '6px',
                backgroundColor: 'var(--mui-palette-error-main)',
                color: 'var(--mui-palette-error-contrastText)',
              }}
            >
              <Typography variant='caption'>{error}</Typography>
            </Box>
          )}
        </Stack>

        {/* Divider */}
        <Divider orientation='vertical' flexItem />

        {/* RIGHT PANEL — Chart preview */}
        <Stack
          sx={{ width: '50%', height: '100%', minWidth: 0, overflow: 'hidden' }}
        >
          {/* Chart toolbar */}
          <Stack
            direction='row'
            alignItems='center'
            gap='8px'
            sx={{ px: '16px', py: '8px', minHeight: '44px' }}
          >
            <ChartTypePicker
              value={wizardState.chartType}
              onChange={(chartType) =>
                setWizardState((s) => ({ ...s, chartType }))
              }
            />
            <Box sx={{ flexGrow: 1 }} />
            {columns.length > 0 && (
              <ColumnMappingBar
                columns={columns}
                data={queryData}
                columnMapping={wizardState.columnMapping}
                chartType={wizardState.chartType}
                onChange={(columnMapping) =>
                  setWizardState((s) => ({ ...s, columnMapping }))
                }
              />
            )}
          </Stack>

          {/* Chart area */}
          <Box
            sx={{ flexGrow: 1, p: '16px', minHeight: 0, overflow: 'hidden' }}
          >
            {queryData.length > 0 ? (
              <ChartRenderer
                chartType={wizardState.chartType}
                data={queryData}
                columnMapping={wizardState.columnMapping}
                animationKey={animationKey}
              />
            ) : (
              <Stack
                alignItems='center'
                justifyContent='center'
                sx={{ height: '100%', opacity: 0.5 }}
              >
                <i
                  className='material-symbols-bar-chart'
                  style={{
                    fontSize: '48px',
                    color: 'var(--mui-palette-text-secondary)',
                  }}
                />
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mt: '8px' }}
                >
                  Execute a query to see results
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      </Stack>
    </PanelWrapper>
  );
}
